import CiModalError from '@/components/CiModal/CiModalError.vue';
import CiModalSuccess from '@/components/CiModal/CiModalSuccess.vue';
import CiModalUserProfileIncomplete from '@/components/CiModal/CiModalUserProfileIncomplete.vue';

export default function useModalDialog({ campsite: pCampsite = null }: { campsite: MaybeRefOrGetter<any | null> }) {
  const campsite = toRef(pCampsite);

  /********************
   * COMPOSITIONS      *
   ********************/
  const localePath = useLocalePath();
  const { $gettext } = useGettext();
  const { getMsgError } = useSystemMessages();
  const { openModal, closeModal } = useCiModal();

  /********************
   * REFS & VARS       *
   ********************/
  const defaultButtons = computed(() => {
    return [
      {
        label: $gettext('Back to campsite'),
        to: localePath({
          name: RBN_CAMPSITE_DETAIL,
          params: { identifier: campsite.value?.slug },
        }),
        variant: 'text',
      },
    ];
  });

  /********************
   * FUNCTIONS         *
   ********************/
  function openModalError(
    buttons: any[] = [],
    title = $gettext('Error'),
    errorMessage = $gettext('Something went wrong.'),
  ) {
    openModal(
      {
        component: CiModalError,
        attrs: {
          name: 'CiModalError',
          closeable: false,
          title,
          buttons,
          errorMessage,
          onClose: () => {
            closeModal('CiModalError');
          },
        },
      },
      {
        clickToClose: false,
        contentTransition: 'translate-y-down',
        contentClass: 'max-w-[800px] w-full h-auto',
      },
    );
  }

  function openModalSuccess(
    buttons = [],
    warnings = [],
    title = $gettext('Success'),
    successMessage = $gettext('Your operation has been completed successfully.'),
  ) {
    openModal(
      {
        component: CiModalSuccess,
        attrs: {
          name: 'CiModalSuccess',
          buttons,
          closeable: false,
          successMessage,
          title,
          warnings,
          onClose: () => {
            closeModal('CiModalSuccess');
          },
        },
      },
      {
        clickToClose: false,
        contentTransition: 'translate-y-down',
        contentClass: 'max-w-[800px] w-full h-auto',
      },
    );
  }

  function openModalMissingPermissions(perm, additionalInfo = null, buttons = defaultButtons.value) {
    openModalError(buttons, $gettext('Permission error'), getMsgError(perm.reason.code, additionalInfo));
  }

  function openModalUserProfileIncomplete(
    fields,
    text = $gettext('Please complete your user data to perform this operation:'),
    backOnCancel = true,
  ) {
    openModal(
      {
        component: CiModalUserProfileIncomplete,
        attrs: {
          name: 'CiModalUserProfileIncomplete',
          backOnCancel,
          closeable: false,
          fields,
          text,
          onClose: () => {
            closeModal('CiModalUserProfileIncomplete');
          },
        },
      },
      {
        clickToClose: false,
        contentTransition: 'translate-y-down',
        contentClass: 'max-w-[800px] w-full h-auto',
      },
    );
  }

  return {
    openModalError,
    openModalMissingPermissions,
    openModalSuccess,
    openModalUserProfileIncomplete,
  };
}
